import { useState } from "react";
import { Modal } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions } from "../lib/store";
import { asbuiltCheck, type DeviationWire } from "../lib/ai";
import { imageForScan } from "../lib/drawingScan";
import { fileToDataUri } from "../lib/image";
import type { Project } from "../lib/types";

/* Compare a site photo with the drawing it should match and flag differences.
   Findings are advisory — each can be raised as a snag with one click. */
const SEV_HUE: Record<string, string> = { high: "var(--danger)", medium: "var(--warn)", low: "var(--ink-3)" };

export function AsBuiltModal({ p, by, onClose }: { p: Project; by: string; onClose: () => void }) {
  const drawn = (p.deliverables ?? []).filter((d) => d.previewUrl && !d.archived);
  const [sheetId, setSheetId] = useState(drawn[0]?.id ?? "");
  const [photo, setPhoto] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<{ summary: string; deviations: DeviationWire[] } | null>(null);
  const [raised, setRaised] = useState<Set<number>>(new Set());
  const sheet = drawn.find((d) => d.id === sheetId);

  async function pickPhoto(f: File | null) {
    if (!f) return;
    setBusy(true); setErr(null);
    try { setPhoto(await fileToDataUri(f, 1600, 0.85)); } catch { setErr("Couldn't read that photo."); }
    setBusy(false);
  }

  async function run() {
    if (!sheet?.previewUrl || !photo) return;
    setBusy(true); setErr(null); setResult(null);
    const drawing = await imageForScan(sheet.previewUrl, 1600);
    if (!drawing) { setBusy(false); setErr("Couldn't load that drawing."); return; }
    const res = await asbuiltCheck(drawing, photo, sheet.code, sheet.title);
    setBusy(false);
    if (!res.ok) { setErr(res.reason === "no-key" ? "The as-built check isn't switched on (needs an AI key)." : "Couldn't run the comparison."); return; }
    setResult({ summary: res.summary ?? "", deviations: res.deviations ?? [] });
    setRaised(new Set());
  }

  function raiseSnag(d: DeviationWire, i: number) {
    actions.addSnag(p.id, {
      title: `${d.element} — differs from ${sheet?.code ?? "drawing"}`,
      note: `Drawing shows: ${d.drawn}\nOn site: ${d.observed}\n(AI as-built check, ${d.confidence} confidence — verify on site.)`,
      location: sheet?.code, severity: d.severity, photos: photo ? [photo] : [], by,
    });
    setRaised((s) => new Set(s).add(i));
  }

  return (
    <Modal open onClose={busy ? () => {} : onClose} title="As-built deviation check" wide>
      <div className="col" style={{ gap: 14 }}>
        <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.55 }}>
          Compare a site photo with the drawing it should match. Findings are a prompt to look, not a verdict — a photo shows one viewpoint, and work in progress legitimately looks unfinished.
        </p>
        <div className="row wrap" style={{ gap: 12 }}>
          <div className="field" style={{ flex: 1, minWidth: 220 }}>
            <label>Drawing</label>
            <select className="select" value={sheetId} onChange={(e) => { setSheetId(e.target.value); setResult(null); }} disabled={!drawn.length}>
              {drawn.length === 0 && <option>No drawn sheets</option>}
              {drawn.map((d) => <option key={d.id} value={d.id}>{d.code} — {d.title}</option>)}
            </select>
          </div>
          <div className="field" style={{ flex: 1, minWidth: 200 }}>
            <label>Site photo</label>
            <label className="panel row between" style={{ padding: "9px 12px", borderStyle: "dashed", cursor: "pointer" }}>
              <span style={{ fontSize: 13 }}>{photo ? "Photo selected" : "Choose a photo"}</span>
              <span className="btn soft sm">Browse</span>
              <input type="file" accept="image/*" hidden disabled={busy} onChange={(e) => pickPhoto(e.target.files?.[0] ?? null)} />
            </label>
          </div>
        </div>
        {(sheet?.previewUrl || photo) && (
          <div className="row" style={{ gap: 10 }}>
            {sheet?.previewUrl && <div style={{ flex: 1 }}><div className="kicker" style={{ fontSize: 8.5, marginBottom: 4 }}>Drawing</div><img src={sheet.previewUrl} alt="" style={{ width: "100%", height: 130, objectFit: "contain", background: "#fff", borderRadius: 8, border: "1px solid var(--line)" }} /></div>}
            {photo && <div style={{ flex: 1 }}><div className="kicker" style={{ fontSize: 8.5, marginBottom: 4 }}>Site</div><img src={photo} alt="" style={{ width: "100%", height: 130, objectFit: "cover", borderRadius: 8, border: "1px solid var(--line)" }} /></div>}
          </div>
        )}
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button className="btn accent" disabled={busy || !sheet?.previewUrl || !photo} onClick={run}><Icon.spark width={15} />{busy ? "Comparing…" : "Compare"}</button>
        </div>
        {err && <div style={{ color: "var(--danger)", fontSize: 12.5 }}>{err}</div>}
        {result && (
          <>
            <div className="panel" style={{ padding: "11px 14px" }}>
              <div className="kicker" style={{ fontSize: 8.5, marginBottom: 4 }}>Summary</div>
              <div style={{ fontSize: 13.5, lineHeight: 1.6 }}>{result.summary}</div>
            </div>
            {result.deviations.length === 0 ? (
              <div className="panel" style={{ padding: 18, textAlign: "center" }}><Icon.check width={20} style={{ color: "var(--ok)" }} /><div style={{ fontWeight: 600, marginTop: 6 }}>No differences reported</div></div>
            ) : (
              <div className="col" style={{ gap: 8, maxHeight: "40vh", overflowY: "auto" }}>
                {result.deviations.map((d, i) => (
                  <div key={i} className="panel" style={{ padding: "10px 13px", borderLeft: `3px solid ${SEV_HUE[d.severity]}` }}>
                    <div className="row between wrap" style={{ gap: 8, marginBottom: 4 }}>
                      <span className="row" style={{ gap: 8 }}>
                        <span className="badge" style={{ fontSize: 8.5, color: SEV_HUE[d.severity] }}>{d.severity}</span>
                        <b style={{ fontSize: 13.5 }}>{d.element}</b>
                        <span className="badge" style={{ fontSize: 8.5 }}>{d.confidence} confidence</span>
                      </span>
                      {raised.has(i)
                        ? <span className="badge ok" style={{ fontSize: 9 }}><Icon.check width={9} />Snag raised</span>
                        : <button className="btn ghost sm" onClick={() => raiseSnag(d, i)}><Icon.plus width={12} />Raise snag</button>}
                    </div>
                    <div style={{ fontSize: 12.5, lineHeight: 1.5 }}><span className="dim">Drawn:</span> {d.drawn}</div>
                    <div style={{ fontSize: 12.5, lineHeight: 1.5 }}><span className="dim">On site:</span> {d.observed}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
